import * as React from 'react';

interface FileDropTargetProps {
    allowedExtensions?: string[];                     // '.csv', etc
    children?: React.ReactNode; // child elements to render inside container
    className?: string;                               // className to give parent DIV
    dragOverClassName?: string;                       // className to give parent DIV on dragover
    dropEffect?: string;                              // drop effect to display on file drag
    onFileSelect?: (files: File[]) => void;            // callback when files dropped/picked
}

interface FileDropTargetState {
    dragOver: boolean; // whether or not DOM element is currently being dragged over
}

class FileDropTarget extends React.PureComponent<FileDropTargetProps, FileDropTargetState> {
    private _fileInput: HTMLInputElement | null;

    constructor(props: FileDropTargetProps) {
        super(props);
        this.state = Object.assign({}, this.state, { dragOver: false });

        this._onDragLeave = this._onDragLeave.bind(this);
        this._onDragOverEnter = this._onDragOverEnter.bind(this);
        this._onDrop = this._onDrop.bind(this);
        this._onFileSelect = this._onFileSelect.bind(this);
    }

    // Show the file picker dialog (if want to connect an external button)
    showFilePicker(): void {
        if (this._fileInput) {
            this._fileInput.click();
        }
    }

    render(): React.ReactNode {
        const { allowedExtensions, children, className, dragOverClassName } = this.props;
        const { dragOver } = this.state;

        const classString = [
            className || '',
            !!dragOverClassName && dragOver ? dragOverClassName : ''
        ].join(' ').trim();

        const showPicker = () => this.showFilePicker();
        return (
            <div
                className={classString}
                onClick={showPicker}
                onDragEnter={this._onDragOverEnter}
                onDragOver={this._onDragOverEnter}
                onDragLeave={this._onDragLeave}
                onDrop={this._onDrop}
            >
                {children}
                <input
                    accept={allowedExtensions ? allowedExtensions.join(',') : undefined}
                    id="fileDropTargetInput"
                    name="fileDropTargetInput"
                    onChange={this._onFileSelect}
                    ref={ref => this._fileInput = ref}
                    style={{display: 'none'}}
                    type="file"
                />
            </div>
        );
    }

    private _onDragOverEnter(e: React.SyntheticEvent<HTMLDivElement>): void {
        if (!e) {
            return;
        }

        const dataTransfer = (e as React.DragEvent<HTMLDivElement>).dataTransfer;

        const draggingFile = dataTransfer && dataTransfer.types &&
            [...dataTransfer.types].indexOf('Files') !== -1;
        if (draggingFile) {
            const { dropEffect } = this.props;

            e.preventDefault();
            dataTransfer.dropEffect = dropEffect || 'copy';
            this.setState({dragOver: true});
        }
    }

    private _onDragLeave(): void {
        this.setState({dragOver: false});
    }

    private _onDrop(e: React.SyntheticEvent<HTMLElement>): void {
        if (!e) {
            return;
        }

        e.preventDefault();

        const { onFileSelect } = this.props;

        const dataTransfer = (e as React.DragEvent<HTMLDivElement>).dataTransfer;
        const draggingFile = dataTransfer && dataTransfer.types &&
            [...dataTransfer.types].indexOf('Files') !== -1;
        if (draggingFile &&
            dataTransfer.files &&
            dataTransfer.files.length &&
            onFileSelect) {
            onFileSelect(Array.from(dataTransfer.files));
        }

        this.setState({dragOver: false});
    }

    private _onFileSelect(e: React.SyntheticEvent<HTMLInputElement>): void {
        if (!e?.target) {
            return;
        }

        const target = (e as React.ChangeEvent<HTMLInputElement>).target;
        const { onFileSelect } = this.props;
        if (target.files &&
            target.files.length &&
            onFileSelect) {
            onFileSelect(Array.from(target.files));
        }
    }
}

export { FileDropTarget };
export default FileDropTarget;



// WEBPACK FOOTER //
// ./src/components/common/fileDropTarget.tsx